import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SlipProvider } from "@/context/SlipContext";
import { Navbar } from "@/components/Navbar";
import { AuthCallback } from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Builder, { BuilderLocked } from "@/pages/Builder";
import MyParlays from "@/pages/MyParlays";
import Performance from "@/pages/Performance";
import Leaderboard from "@/pages/Leaderboard";
import Pricing from "@/pages/Pricing";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Billing from "@/pages/Billing";
import Ticket from "@/pages/Ticket";
import Favorites from "@/pages/Favorites";
import Props from "@/pages/Props";
import Players from "@/pages/Players";
import Player from "@/pages/Player";
import PlayerCardPage from "@/pages/PlayerCardPage";
import TeamCardPage from "@/pages/TeamCardPage";
import { MobileSlip } from "@/components/MobileSlip";
import { InstallBanner } from "@/components/InstallBanner";
import { AskLab } from "@/components/AskLab";
import { Onboarding } from "@/components/Onboarding";
import Game from "@/pages/Game";
import TrackRecord from "@/pages/TrackRecord";
import Contests from "@/pages/Contests";
import Standings from "@/pages/Standings";
import Team from "@/pages/Team";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined)
    return (
      <div className="flex h-[60vh] items-center justify-center text-slate-400" data-testid="auth-loading">
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 pulse-dot mr-3" /> Verifying session…
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Navbar />
      <InstallBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/favorites" element={<Protected><Favorites /></Protected>} />
        <Route path="/props" element={<Protected><Props /></Protected>} />
        <Route path="/players" element={<Protected><Players /></Protected>} />
        <Route path="/players/:slug" element={<Protected><Player /></Protected>} />
        <Route path="/builder" element={<Protected><Builder /></Protected>} />
        <Route path="/builder-locked" element={<Protected><BuilderLocked /></Protected>} />
        <Route path="/parlays" element={<Protected><MyParlays /></Protected>} />
        <Route path="/performance" element={<Protected><Performance /></Protected>} />
        <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/billing" element={<Protected><Billing /></Protected>} />
        <Route path="/t/:id" element={<Ticket />} />
        <Route path="/p/:slug" element={<PlayerCardPage />} />
        <Route path="/t/:sport/:slug" element={<TeamCardPage />} />
        <Route path="/track-record" element={<TrackRecord />} />
        <Route path="/games/:id" element={<Protected><Game /></Protected>} />
        <Route path="/contests" element={<Protected><Contests /></Protected>} />
        <Route path="/standings" element={<Protected><Standings /></Protected>} />
        <Route path="/teams/:sport/:slug" element={<Protected><Team /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <MobileSlip />
      <AskLab />
      <Onboarding />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SlipProvider>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" richColors />
        </SlipProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
